// Stock photos (Flickr, Wikimedia) chosen for a place (2026-10-17): keep the
// print-size image and who to credit, so a Photo Book made from them prints
// sharp and can list its photo credits. The image chooser only showed the
// licence and photographer; picking a photo kept just the screen-size URLs.
//
// Place fields (both optional, cleared when a photo without them replaces
// the photo):
//   PhotoPrint  — URL of the largest available version, for printing
//   PhotoCredit — { author, license, source, pageUrl }

// Flickr licence ids a printed (and sold) book can use: CC BY, CC BY-SA,
// "No known copyright restrictions", US Government work, CC0, Public domain.
// Leaves out All rights reserved (0), NonCommercial (1, 2, 3) and
// NoDerivatives (3, 6 — cropping a photo onto a page can count as a change).
export const FLICKR_PRINT_LICENSES = "4,5,7,8,9,10";

// Public domain and similar: no credit required (still listed as a courtesy).
const NO_CREDIT_NEEDED = /^(cc0|public domain|pd\b|no known (copyright )?restrictions|us government work)/i;

export const creditNeeded = (credit) => !!credit && !NO_CREDIT_NEEDED.test(String(credit.license || "").trim());

// The credit as one line, e.g. "Jane Doe · CC BY 2.0 · Flickr".
export const creditLine = (credit) => {
  if (!credit) return "";
  return [credit.author || "Unknown photographer", credit.license, credit.source].filter(Boolean).join(" · ");
};

// The fields an image chooser item carries over to a place. The item comes
// from the image dialog (url = small, url_m = screen size; Flickr/Wikimedia
// items also carry url_print and credit).
export const photoFieldsFromImage = (item) => {
  if (!item) return {};
  const fields = { Photo: item.url, Photo_m: item.url_m };
  if (item.url_print && item.url_print !== item.url_m) fields.PhotoPrint = item.url_print;
  if (item.credit && (item.credit.author || item.credit.license)) fields.PhotoCredit = { ...item.credit };
  return fields;
};

// Sets the print image and credit on `place` from an image chooser item, or
// clears them (a new photo must not keep the old one's credit).
export const applyPhotoExtras = (place, item) => {
  if (!place) return;
  const f = photoFieldsFromImage(item);
  if (f.PhotoPrint) place.PhotoPrint = f.PhotoPrint;
  else delete place.PhotoPrint;
  if (f.PhotoCredit) place.PhotoCredit = f.PhotoCredit;
  else delete place.PhotoCredit;
};

// The Photo Book's print floor: 21 cm at ~150 DPI ≈ 1240 px on the short
// side (PhotoBook.js). Search results show each photo's size against it so a
// photo that would print blurry can be left out before it's picked.
export const PRINT_MIN_PX = 1240;
export const isLowResForPrint = (w, h) => !!w && !!h && Math.min(w, h) < PRINT_MIN_PX;
// "4000 × 3000" or "⚠ 800 × 600 — low resolution"; "" when unknown.
export const printSizeLabel = (w, h) => (!w || !h ? "" : `${isLowResForPrint(w, h) ? "⚠ " : ""}${w} × ${h}${isLowResForPrint(w, h) ? " — low resolution" : ""}`);

// Flickr returns url_<size> only for the sizes that exist; the largest one
// that isn't the (possibly huge, possibly rotated) original comes first.
export const flickrPrintUrl = (photo) => photo.url_k || photo.url_h || photo.url_o || photo.url_l || null;
export const flickrPrintSize = (photo) => {
  for (const s of ["k", "h", "o", "l"]) {
    if (photo[`url_${s}`]) return { width: Number(photo[`width_${s}`]) || 0, height: Number(photo[`height_${s}`]) || 0 };
  }
  return { width: 0, height: 0 };
};

// The same picture at a different address counts as the same image
// (2026-10-18) — Place Details showed a place's photo twice when its notes
// began with another size of it: a Wikimedia thumbnail vs the original or
// 1280px version, Flickr _z vs _b, our own uploads' _t/_m/_l files, or an
// address wrapped by the image proxy.
export const sameImageKey = (url) => {
  let s = String(url || "").trim();
  if (!s) return "";
  // img_proxy.php?url=<encoded address> -> the address itself
  const proxied = /[?&]url=([^&]+)/.exec(s);
  if (/img_proxy/i.test(s) && proxied) { try { s = decodeURIComponent(proxied[1]); } catch (e) { /* keep */ } }
  try { s = decodeURI(s); } catch (e) { /* keep as is */ }
  s = s.replace(/^http:\/\//i, "https://");
  // Wikimedia: .../thumb/a/ab/File.jpg/800px-File.jpg and .../a/ab/File.jpg
  const wm = /(?:upload|thumb)\.wikimedia\.org\/wikipedia\/[^/]+\/(?:thumb\/)?[0-9a-f]\/[0-9a-f]{2}\/([^/?#]+)/i.exec(s);
  if (wm) return `wm:${wm[1].toLowerCase()}`;
  // Flickr: .../<server>/<id>_<secret>[_<size>].jpg
  const fl = /staticflickr\.com\/(?:\d+\/)*(\d+)_[0-9a-f]+(?:_[a-z0-9]+)?\.[a-z]+/i.exec(s);
  if (fl) return `fl:${fl[1]}`;
  // Google-hosted photos: size options after "="
  if (/googleusercontent\.com/i.test(s)) return `gu:${s.replace(/=[^/?#]*$/, "")}`;
  // Our uploads: <base>_t / _m / _l share one base name; the query is only
  // an access token there. Elsewhere the query can be the identity (e.g.
  // ?photoreference=), so it stays.
  if (/firebasestorage\.googleapis\.com/i.test(s)) return s.replace(/[?#].*$/, "").replace(/_(t|m|l)(\.[a-z0-9]+)$/i, "$2");
  return s;
};
export const isSameImage = (a, b) => !!a && !!b && sameImageKey(a) === sameImageKey(b);
